import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import { Add, Remove, Check, Close, Architecture } from '@mui/icons-material';

type SurfaceJointDualSpinnerProps = {
  value1: number;
  value2: number;
  displayedValue?: 'value1' | 'value2';
  onChange: (newValue: number) => void;
  jointName?: string;
  label1?: string;
  label2?: string;
  min?: number;
  max?: number;
  step?: number;
  showNumber?: boolean;
  icon?: React.ReactNode;
  color?: string;
  onOpen?: () => void;
  onClose?: () => void;
};

const SurfaceJointDualSpinner: React.FC<SurfaceJointDualSpinnerProps> = ({
  value1,
  value2,
  displayedValue = 'value1',
  onChange,
  jointName,
  label1 = 'Value 1',
  label2 = 'Value 2',
  min = 0,
  max = 100,
  step = 1,
  showNumber = true,
  color = 'var(--toolbar-button-border)',
  icon = (
    <Architecture
      sx={{
        fontSize: 24,
        transform: 'rotate(-90deg)',
      }}
    />
  ),
  onOpen,
  onClose,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value1);
  const holdTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const themeColor = color;
  const shownValue = displayedValue === 'value1' ? value1 : value2;

  const editableLabel = label1;
  const otherLabel = label2;
  const editableMin = min;
  const editableMax = max;
  const editableStep = step;
  const otherValue = value2;
  const otherValueLabel = otherLabel;

  const getStepDecimals = (s: number): number => {
    if (s % 1 === 0) return 0;
    const str = s.toString();
    const parts = str.split('.');
    return parts[1]?.length ?? 0;
  };

  const stepDecimals = getStepDecimals(editableStep);

  const clampWithStep = (v: number): number => {
    const factor = Math.pow(10, stepDecimals);
    const stepsFromMin = Math.round((v - editableMin) * factor);
    const stepInt = Math.round(editableStep * factor);
    const snappedSteps = Math.round(stepsFromMin / stepInt) * stepInt;
    let result = editableMin + snappedSteps / factor;
    result = Math.max(editableMin, Math.min(editableMax, result));
    return parseFloat(result.toFixed(stepDecimals));
  };

  const formatNumber = (v: number): string => {
    if (editableStep % 1 === 0) {
      return Math.round(v).toFixed(1);
    }
    const decimalPlaces = editableStep.toString().split('.')[1]?.length || 0;
    return v.toFixed(decimalPlaces);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    if (!/^-?\d*\.?\d*$/.test(raw)) return;
    const parsed = parseFloat(raw);
    if (isNaN(parsed)) {
      setTempValue(editableMin);
      return;
    }
    const nextValue = clampWithStep(parsed);
    setTempValue(nextValue);
  };

  const handleApply = () => {
    onChange(tempValue);
    setIsEditing(false);
    onClose?.();
  };

  const handleCancel = () => {
    setTempValue(value1);
    setIsEditing(false);
    onClose?.();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEditing &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setTempValue(value1);
        setIsEditing(false);
        onClose?.();
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
    };
  }, [isEditing, value1, value2, displayedValue, onClose]);

  const startHold = (direction: 'inc' | 'dec') => {
    stopHold();

    const action = () => {
      setTempValue((prev) => {
        const nextValue = clampWithStep(prev + (direction === 'inc' ? editableStep : -editableStep));
        return nextValue;
      });
    };

    action();

    holdTimeout.current = setTimeout(() => {
      holdInterval.current = setInterval(action, 60);
    }, 300);
  };

  const stopHold = () => {
    if (holdTimeout.current) {
      clearTimeout(holdTimeout.current);
      holdTimeout.current = null;
    }
    if (holdInterval.current) {
      clearInterval(holdInterval.current);
      holdInterval.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopHold();
    };
  }, []);

  useEffect(() => {
    if (!isEditing) {
      setTempValue(value1);
    }
  }, [value1, displayedValue, isEditing]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'inline-block',
        transition: 'all 0.15s ease',
      }}
    >
      {!isEditing ? (
        <Typography
          component="div"
          data-voice-open="1"
          onClick={() => {
            setTempValue(value1);
            setIsEditing(true);
            onOpen?.();
          }}
        >
          {showNumber ? (
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 'bold',
                color: themeColor,
                fontFamily: 'monospace',
                border: `1px solid ${themeColor}`,
                cursor: 'pointer',
                textAlign: 'center',
                padding: '1px 4px',
                borderRadius: '20px',
                bgcolor: 'var(--spinner-bg)',
                backdropFilter: 'blur(8px)',
                whiteSpace: 'nowrap',
                alignItems: 'center',
                '&:hover': {
                  bgcolor: 'var(--spinner-hover-bg)',
                },
              }}
            >
              {formatNumber(shownValue)}
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${themeColor}`,
                borderRadius: '50%',
                bgcolor: 'var(--spinner-bg)',
                width: 26,
                height: 26,
                cursor: 'pointer',
                transition: '0.15s ease',
                color: themeColor,
                '&:hover': {
                  bgcolor: 'var(--spinner-hover-bg)',
                },
              }}
            >
              {icon}
            </Box>
          )}
        </Typography>
      ) : (
        <Box
          sx={{
            bgcolor: 'var(--spinner-bg)',
            backdropFilter: 'blur(8px)',
            borderRadius: '12px',
            border: `1px solid ${themeColor}`,
            padding: '0px 10px 6px 10px',
            minWidth: '220px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            transition: '0.15s ease',
          }}
        >
          {/* <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 500,
                color: themeColor,
                letterSpacing: '0.3px',
                fontFamily: 'monospace',
                textAlign: 'center',
                width: '100%',
              }}
            >
              {jointName}
            </Typography>
          </Box> */}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1.2,
            }}
          >
            <IconButton
              size="medium"
              data-voice-close="1"
              onClick={handleCancel}
              sx={{ color: '#d32f2f', width: 18, height: 18, mb: 0.5 }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>

            <Box sx={{ display: 'flex', gap: 1.5, flex: 1, alignItems: 'flex-end' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.3,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    color: themeColor,
                  }}
                >
                  {editableLabel}
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    bgcolor: 'var(--spinner-input-bg)',
                    borderRadius: '20px',
                    padding: '1px',
                  }}
                >
                  <IconButton
                    size="small"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      startHold('dec');
                    }}
                    onPointerUp={stopHold}
                    onPointerLeave={stopHold}
                    onPointerCancel={stopHold}
                    disabled={tempValue <= editableMin}
                    sx={{
                      bgcolor: 'transparent',
                      color: themeColor,
                      width: 24,
                      height: 24,
                      borderRadius: '12px',
                    }}
                  >
                    <Remove sx={{ fontSize: 14 }} />
                  </IconButton>

                  <TextField
                    type="text"
                    value={formatNumber(tempValue)}
                    onChange={handleInputChange}
                    variant="standard"
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        width: '40px',
                        '& input': {
                          textAlign: 'center',
                          padding: '2px 0',
                          fontSize: '11px',
                          color: themeColor,
                          bgcolor: 'transparent',
                          fontFamily: 'monospace',
                          fontWeight: 500,
                        },
                      },
                    }}
                  />

                  <IconButton
                    size="small"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      startHold('inc');
                    }}
                    onPointerUp={stopHold}
                    onPointerLeave={stopHold}
                    onPointerCancel={stopHold}
                    disabled={tempValue >= editableMax}
                    sx={{
                      bgcolor: 'transparent',
                      color: themeColor,
                      width: 24,
                      height: 24,
                      borderRadius: '12px',
                    }}
                  >
                    <Add sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.3,
                  p: '8px 6px',
                  borderRadius: '10px',
                  bgcolor: 'rgba(255,255,255,0.08)',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    color: themeColor,
                  }}
                >
                  {otherValueLabel}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: themeColor,
                    fontFamily: 'monospace',
                  }}
                >
                  {formatNumber(otherValue)}
                </Typography>
              </Box>
            </Box>

            <IconButton
              size="small"
              onClick={handleApply}
              sx={{ color: '#2e7d32', width: 18, height: 18, mb: 0.5 }}
            >
              <Check sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SurfaceJointDualSpinner;
